import React, {useEffect, useMemo, useState} from 'react';
import {collection, deleteDoc, doc, getDoc, getDocs, setDoc, updateDoc} from "firebase/firestore";
import {Button, Field, Input, Label, Popover, PopoverButton, PopoverPanel, Select, Switch} from "@headlessui/react";
import {
    ArchiveBoxIcon, ArrowDownIcon,
    ArrowPathIcon, ArrowUpIcon, DocumentArrowDownIcon, LinkSlashIcon, QueueListIcon, RectangleGroupIcon,
} from "@heroicons/react/24/outline";
import {db} from "../firebase/firebase";
import {useAuth} from "../contexts/authContext";
import {useDarkMode} from "../contexts/darkModeContext";
import {ChevronDownIcon} from "@heroicons/react/16/solid";
import course from "../PFC_Images/course.jpg";
import fullLogo from "../PFC_Images/Plantfood Logo.png"
import Loading from "./Widgets/Loading";
import OrderSection from "./Orders/OrderSection";
import {
    classNames,
    downloadExcel,
    formatUnixTimestampToReadableString,
    getQuarterString,
    numberWithCommas
} from "./GLOBAL/GlobalFunctions";
import notifySound from "../notification.mp3";
// import santaSound from "../santa.mp3"
// import bonesSound from "../bones.mp3"
import NewOrder from "./OrderForm/NewOrder";
import OrderSearchInput from "./Orders/OrderSearchInput";
import {useNotification} from "./Notifications/NotificationProvider";
import {countUniqueCustomerCodes} from "./GLOBAL/OrderFunctions";
import PlantLoading from "./PlantLoading";
import {mapWithConcurrency} from "./GLOBAL/promiseUtils";
import CustomButton from "./Widgets/CustomButton";
import ManualOrderArchiver from "./Orders/ManualOrderArchiver";




const PlantOrders = () => {
    const { currentUser } = useAuth();
    
    const [alertsOn] = useState(() => {
        const storedSettings = localStorage.getItem('alertsOn');
        return storedSettings ? JSON.parse(storedSettings).alertsOn : false; // Default to false if null
    });

    const [status, setStatus] = useState("P");

    const { darkMode } = useDarkMode();
    const { triggerNotification } = useNotification();

    const [allOrders, setAllOrders] = useState([]);
    const [archiveOrders, setArchiveOrders] = useState(null);
    const handleArchived = (removed) => {
        const keys = new Set(removed.map(order => JSON.stringify([order.userId, order.clientId, order.id])));
        const keep = order => !keys.has(JSON.stringify([order.userId, order.clientId, order.id]));
        setAllOrders(orders => orders.filter(keep));
        setFilteredAllOrders(orders => orders.filter(keep));
        setArchiveOrders(orders => orders.filter(keep));
        // Invalidate cached active lists so other views reload the saved data.
        try {
            localStorage.removeItem(`allOrders_${currentUser.uid}`);
            localStorage.removeItem(`activeOrders_${currentUser.uid}`);
        } catch (error) {
            console.error('Unable to clear order cache', error);
        }
    };
    const [filteredAllOrders, setFilteredAllOrders] = useState([]);

    const [allPending, setAllPending] = useState([]);
    const [fileredAllPending, setFilteredAllPending] = useState([]);

    const [searchQuery, setSearchQuery] = useState("");
    const [reps, setReps] = useState([]);
    const [fname, setFname] = useState("");

    useEffect(() => {
        const fetchUserName = async () => {
            if (currentUser) {
                try {
                    const storedUserData = localStorage.getItem(`userData_${currentUser.uid}`);
                    if (storedUserData) {
                        const userData = JSON.parse(storedUserData);
                        setReps(userData.reps || []);
                        setFname(userData.fname);
                    } else {
                        const userDoc = await db.collection('Users').doc(currentUser.uid).get();
                        if (userDoc.exists) {
                            const userData = userDoc.data();
                            setReps(userData.reps || []);
                            setFname(userData.fname);
                            localStorage.setItem(`userData_${currentUser.uid}`, JSON.stringify(userData));
                        } else {
                            console.log('User document not found');
                        }
                    }
                } catch (error) {
                    console.error('Error fetching user data:', error);
                }
            }
        };
        

        fetchUserName();
    }, [currentUser]);

    useEffect(() => {

        const fetchAllUsersPendingData = async () => {
            try {
                if (!reps || reps.length === 0) {
                    console.log("Reps is null or empty. Returning an empty array.");
                    setAllPending([]);
                    setFilteredAllPending([]);
                    return;
                }
        
                const storedPendingData = localStorage.getItem(`allPendingOrders_${currentUser.uid}`);
                if (storedPendingData) {
                    const pendingData = JSON.parse(storedPendingData);
                    // Sort in descending order before setting state
                    pendingData.sort((a, b) => new Date(b.submittedTime) - new Date(a.submittedTime));
                    setAllPending(pendingData);
                    setFilteredAllPending(pendingData);
                } else {
                    const repsIds = reps.map((rep) => rep.id);
                    let allPendingData = [];
        
                    const userResults = await mapWithConcurrency(repsIds, 2, async (userId) => {
                        const clientsCollectionRef = collection(db, `Customers/${userId}/clients`);
                        const clientsSnapshot = await getDocs(clientsCollectionRef);
        
                        const pendingResults = await mapWithConcurrency(clientsSnapshot.docs, 8, async (clientDoc) => {
                            const clientId = clientDoc.id;
                            const pendingCollectionRef = collection(db, `Customers/${userId}/clients/${clientId}/pending`);
                            const pendingSnapshot = await getDocs(pendingCollectionRef);
        
                            return pendingSnapshot.docs.map((pendingDoc) => ({
                                    userId,
                                    clientId,
                                    ...pendingDoc.data(),
                                    id: pendingDoc.id,
                            }));
                        });
                        return pendingResults.flat();
                    });
                    allPendingData = userResults.flat();
        
                    if (allPendingData.length > 0) {
                        // Sort in descending order before setting state
                        allPendingData.sort((a, b) => new Date(b.submittedTime) - new Date(a.submittedTime));
                        setAllPending(allPendingData);
                        setFilteredAllPending(allPendingData);
                        localStorage.setItem(`allPendingOrders_${currentUser.uid}`, JSON.stringify(allPendingData));
                    } else {
                        setAllPending([]);
                        setFilteredAllPending([]);
                        localStorage.removeItem(`allPendingOrders_${currentUser.uid}`);
                        console.log('No pending orders found, clearing data.');
                    }
                }
            } catch (error) {
                console.error("Error fetching pending data for all users: ", error);
            }
        };

        const fetchAllUsersActiveData = async () => {
            try {
                if (!reps || reps.length === 0) {
                    console.log("Reps is null or empty. Returning an empty array.");
                    setAllOrders([]);
                    setFilteredAllOrders([]);
                    return;
                }
        
                const storedAllData = localStorage.getItem(`allOrders_${currentUser.uid}`);
                if (storedAllData) {
                    const activeData = JSON.parse(storedAllData);
                    // Sort in descending order before setting state
                    activeData.sort((a, b) => new Date(b.submittedTime) - new Date(a.submittedTime));
                    setAllOrders(activeData);
                    setFilteredAllOrders(activeData);
                } else {
                    const repsIds = reps.map((rep) => rep.id);
                    let allActiveData = [];
        
                    const userResults = await mapWithConcurrency(repsIds, 2, async (userId) => {
                        const clientsCollectionRef = collection(db, `Customers/${userId}/clients`);
                        const clientsSnapshot = await getDocs(clientsCollectionRef);
        
                        const activeResults = await mapWithConcurrency(clientsSnapshot.docs, 8, async (clientDoc) => {
                            const clientId = clientDoc.id;
                            const activeCollectionRef = collection(db, `Customers/${userId}/clients/${clientId}/active`);
                            const activeSnapshot = await getDocs(activeCollectionRef);
        
                            return activeSnapshot.docs.map((activeDoc) => ({
                                    userId,
                                    clientId,
                                    ...activeDoc.data(),
                                    id: activeDoc.id,
                            }));
                        });
                        return activeResults.flat();
                    });
                    allActiveData = userResults.flat();
        
                    if (allActiveData.length > 0) {
                        // Sort in descending order before setting state
                        allActiveData.sort((a, b) => new Date(b.submittedTime) - new Date(a.submittedTime));
                        setAllOrders(allActiveData);
                        setFilteredAllOrders(allActiveData);
                        localStorage.setItem(`allOrders_${currentUser.uid}`, JSON.stringify(allActiveData));
                    } else {
                        setAllOrders([]);
                        setFilteredAllOrders([]);
                        localStorage.removeItem(`allOrders_${currentUser.uid}`);
                        console.log('No active orders found, clearing data.');
                    }
                }
            } catch (error) {
                console.error("Error fetching active data for all users: ", error);
            }
        };

        fetchAllUsersPendingData();
        fetchAllUsersActiveData();

    }, [currentUser, reps]);


    const [updating, setUpdating] = useState(false);

    
    const [fetchStatus, setFetchStatus] = useState({
        running: false,
        elapsed: 0,
        currentRep: null,
        message: null,
        progress: 0,
    });
    
    
    
    
    
    const [lastUpdated, setLastUpdated] = useState(localStorage.getItem("repOrdersUpdatedLast"));

    const onUpdate = async () => {
      setUpdating(true);
      setFetchStatus({ running: true, elapsed: 0, currentRep: null, message: "Starting..." });

      const startTime = Date.now();
      const timer = setInterval(() => {
        setFetchStatus((prev) => ({
          ...prev,
          elapsed: Math.floor((Date.now() - startTime) / 1000),
        }));
      }, 1000);

      try {
        const userRef = doc(db, "Users", currentUser.uid);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
          console.error("User document not found");
          setUpdating(false);
          setFetchStatus({ running: false });
          clearInterval(timer);
          return;
        }

        const userData = userSnap.data();
        const repsData = userData.reps || [];
        setReps(repsData);

        // Calculate total number of clients across all reps
        let totalClients = 0;
        const clientsByRep = new Map();
        for (const rep of repsData) {
          const clientsSnapshot = await getDocs(collection(db, `Customers/${rep.id}/clients`));
          clientsByRep.set(rep.id, clientsSnapshot.docs);
          totalClients += clientsSnapshot.size;
        }
        let processedClients = 0;

        const fetchOrdersForReps = async (type) => {
          const allOrders = [];

          for (const rep of repsData) {
            setFetchStatus((prev) => ({
              ...prev,
              currentRep: rep.id,
              message: `Fetching ${type} orders...`,
            }));

            const clientDocs = clientsByRep.get(rep.id) || [];
            const results = await mapWithConcurrency(
                clientDocs,
                12,
                async (clientDoc) => {
                  const clientId = clientDoc.id;
                  const subSnap = await getDocs(
                    collection(db, `Customers/${rep.id}/clients/${clientId}/${type}`)
                  );
                  // Progress tracking: increment processedClients for each clientDoc processed
                  processedClients += 1;
                  setFetchStatus((prev) => ({
                    ...prev,
                    progress: totalClients > 0 ? (processedClients / (totalClients * 2)) * 100 : 0,
                  }));
                  return subSnap.docs.map((doc) => ({
                    userId: rep.id,
                    clientId,
                    id: doc.id,
                    ...doc.data(),
                  }));
                }
              );
            allOrders.push(...results.flat());
          }

          return allOrders;
        };

        const [pendingData, activeData] = await Promise.all([
          fetchOrdersForReps("pending"),
          fetchOrdersForReps("active"),
        ]);
        
        
        
        // 3. Handle pending
        if (pendingData.length > 0) {
          console.log(`📊 Pending orders: ${pendingData.length}`);
          setAllPending(pendingData);
          setFilteredAllPending(pendingData);
          localStorage.setItem(
            `allPendingOrders_${currentUser.uid}`,
            JSON.stringify(pendingData)
          );
        } else {
          console.log("📭 No pending orders found.");
          setAllPending([]);
          setFilteredAllPending([]);
          localStorage.removeItem(`allPendingOrders_${currentUser.uid}`);
        }
    
        // 4. Handle active
        if (activeData.length > 0) {
          console.log(`📊 Active orders: ${activeData.length}`);
          activeData.sort(
            (a, b) => new Date(b.submittedTime) - new Date(a.submittedTime)
          );
          setAllOrders(activeData);
          setFilteredAllOrders(activeData);
          localStorage.setItem(
            `allOrders_${currentUser.uid}`,
            JSON.stringify(activeData)
          );
        } else {
          console.log("📭 No active orders found.");
          setAllOrders([]);
          setFilteredAllOrders([]);
          localStorage.removeItem(`allOrders_${currentUser.uid}`);
        }
    
        // 5. Update lastSeen efficiently
        await updateDoc(userRef, { lastSeen: Date.now() });
        console.log("🕒 Updated lastSeen field for user.");
        // 💾 Save locally so UI can read immediately
        localStorage.setItem("repOrdersUpdatedLast", Date.now());
        setLastUpdated(Date.now());
        // ... (keep your existing logic for pendingData/activeData updates)
      } catch (error) {
        console.error("Error updating orders:", error);
      }

      clearInterval(timer);
      setFetchStatus({ running: false });
      setUpdating(false);
    };
    
    
    const formatRelativeTime = (timestamp) => {
        if (!timestamp) return "—";
    
        const now = Date.now();
    
        // ✅ ALWAYS convert first
        const time = Number(timestamp);
    
        if (!time || isNaN(time)) return "—";
    
        const diff = Math.floor((now - time) / 1000);
    
        if (diff < 60) return "Just now";
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return `${Math.floor(diff / 86400)}d ago`;
    };
    
    
    const handlePendingSearch = (query) => {
        setSearchQuery(query);
        
        const lowerCaseQuery = query.toLowerCase();

        const filtered = allPending.filter((order) =>
            order.customerName.toLowerCase().includes(lowerCaseQuery) ||
            order.customerCode.toLowerCase().includes(lowerCaseQuery) ||
            
            order.products.some((prod) => prod.productCode.toLowerCase().includes(query.toLowerCase())) ||
            order.products.some((prod) => prod.name.toLowerCase().includes(query.toLowerCase()))
            // customer.customerPhone.includes(lowerCaseQuery) ||
            // customer.salesCode.toString().includes(lowerCaseQuery) // If orderNumber is a number, convert it to a string
        );

        setFilteredAllPending(filtered);
    }

    const handleActiveSearch = (query) => {
        setSearchQuery(query);
        
        const lowerCaseQuery = query.toLowerCase();

        const filtered = allOrders.filter((order) =>
            order.customerName.toLowerCase().includes(lowerCaseQuery) ||
            order.customerCode.toLowerCase().includes(lowerCaseQuery) ||
            order.orderERP.toString().includes(lowerCaseQuery) || // If orderNumber is a number, convert it to a string
            
            order.products.some((prod) => prod.productCode.toLowerCase().includes(query.toLowerCase())) ||
            order.products.some((prod) => prod.name.toLowerCase().includes(query.toLowerCase()))
        );
        
        setFilteredAllOrders(filtered);
    }


    const filterOrdersByIndustry = (industry) => {
        const filtered = allOrders.filter(customer => industry === '' || customer.customerIndustry === industry);
        setFilteredAllOrders(filtered);
    };

    const filterPendingOrdersByIndustry = (industry) => {
        const filtered = allPending.filter(customer => industry === '' || customer.customerIndustry === industry);
        setFilteredAllPending(filtered);
    };


    const filterOrdersByRep = (rep) => {
        const filtered = allOrders.filter(customer => rep === '' || customer.salesRep === rep);
        setFilteredAllOrders(filtered);
    };

    const filterPendingOrdersByRep = (rep) => {
        const filtered = allPending.filter(customer => rep === '' || customer.salesRep === rep);
        setFilteredAllPending(filtered);
    };


    const filtering = (e) => {
        if(status === "A"){
            filterOrdersByIndustry(e.target.value);
        }
        if (status === "P"){
            filterPendingOrdersByIndustry(e.target.value);
        }
    }

    const filteringRep = (e) => {
        if(status === "A"){
            filterOrdersByRep(e.target.value);
        }
        if (status === "P"){
            filterPendingOrdersByRep(e.target.value);
        }
    }



    // Function to calculate the total grand total from pending orders
    const calculateTotalGrandTotal = (orders) => {
        return orders.reduce((total, order) => {
            return total + (parseFloat(order.grandTotal) || 0); // Add 0 for undefined grandTotal
        }, 0);
    };


    const activeGrandTotal = calculateTotalGrandTotal(filteredAllOrders);
    const pendingGrandTotal = calculateTotalGrandTotal(fileredAllPending);
    
    const [AscDesc, setAscDesc] = useState(true);
    const [AlphaAscDesc, setAlphaAscDesc] = useState(true);
    const [DateAscDesc, setDateAscDesc] = useState(false); // Default to descending order
    // const [DateAscDesc, setDateAscDesc] = useState(true);

    const toggleAscDesc = () => {
        // Toggle AscDesc and sort the orders array
        setAscDesc((prev) => !prev);

        if(status === "A"){
            const sortedOrders = [...filteredAllOrders].sort((a, b) => {
                return AscDesc
                    ? a.grandTotal - b.grandTotal  // Ascending
                    : b.grandTotal - a.grandTotal; // Descending
            });
            setFilteredAllOrders(sortedOrders);
        }
        if (status === "P"){
            const sortedOrders = [...fileredAllPending].sort((a, b) => {
                return AscDesc
                    ? a.grandTotal - b.grandTotal  // Ascending
                    : b.grandTotal - a.grandTotal; // Descending
            });
            setFilteredAllPending(sortedOrders);
        }
    };
    
    const toggleAlphaAscDesc = () => {
        setAlphaAscDesc((prev) => !prev);
    
        if (status === "A") {
            const sortedOrders = [...filteredAllOrders].sort((a, b) => {
                return AlphaAscDesc
                    ? a.customerCode.localeCompare(b.customerCode)  // Ascending
                    : b.customerCode.localeCompare(a.customerCode); // Descending
            });
            setFilteredAllOrders(sortedOrders);
        }
        
        if (status === "P") {
            const sortedPending = [...fileredAllPending].sort((a, b) => {
                return AlphaAscDesc
                    ? a.customerCode.localeCompare(b.customerCode)  // Ascending
                    : b.customerCode.localeCompare(a.customerCode); // Descending
            });
            setFilteredAllPending(sortedPending);
        }
    };
    
    const toggleDateAscDesc = () => {
        setDateAscDesc((prev) => {
            const newOrder = !prev;
            
            if (status === "A") {
                const sortedOrders = [...filteredAllOrders].sort((a, b) => {
                    return newOrder
                        ? new Date(a.orderDate) - new Date(b.orderDate)  // Ascending
                        : new Date(b.orderDate) - new Date(a.orderDate); // Descending
                });
                setFilteredAllOrders(sortedOrders);
            }
    
            if (status === "P") {
                const sortedOrders = [...fileredAllPending].sort((a, b) => {
                    return newOrder
                        ? new Date(a.orderDate) - new Date(b.orderDate)  // Ascending
                        : new Date(b.orderDate) - new Date(a.orderDate); // Descending
                });
                setFilteredAllPending(sortedOrders);
            }
    
            return newOrder;
        });
    };
    

    const statusMap = useMemo(() => ({
        A: {
            orders: filteredAllOrders,
            emptyTitle: "Your Reps Have No Active Orders",
            emptyDescription: "You have to upload your reps pending orders to the ERP",
        },
        P: {
            orders: fileredAllPending,
            emptyTitle: "Your Reps Have No Pending Orders",
            emptyDescription: "You have to wait for your Reps to place orders in the field",
        },
    }), [filteredAllOrders, fileredAllPending]);
    
    const currentStatus = statusMap[status] || {};
    
    
    const localStorageKey = 'updateSettings';

    const [automaticUpdate, setAutomaticUpdate] = useState(() => {
        const storedSettings = localStorage.getItem(localStorageKey);
        return storedSettings ? JSON.parse(storedSettings).automaticUpdate : true;
    });
    
    const localStorageKeyAlert = 'alertSound';

    const [alertSound, setAlertSound] = useState(() => {
        const storedSettings = localStorage.getItem(localStorageKeyAlert);
        return storedSettings ? JSON.parse(storedSettings).alertSound : true;
    });
    
    const [updateInterval, setUpdateInterval] = useState(10);
    
    // const [updateInterval, setUpdateInterval] = useState(() => {
    //     const storedSettings = localStorage.getItem(localStorageKey);
    //     return storedSettings ? JSON.parse(storedSettings).updateInterval : 0;
    // });
    
    // Save settings to local storage when they change
    useEffect(() => {
        localStorage.setItem(
            localStorageKey,
            JSON.stringify({ automaticUpdate, updateInterval })
        );
        console.log("Changes Saved");
    }, [automaticUpdate, updateInterval, archiveOrders]);
    
    useEffect(() => {
        localStorage.setItem(
            localStorageKeyAlert,
            JSON.stringify({ alertSound })
        );
        console.log("Changes Saved");
    }, [alertSound]);

    const [timeLeft, setTimeLeft] = useState(updateInterval * 60); // Initial countdown in seconds
    
    useEffect(() => {
        let intervalId;
        let countdownId;
    
        if (automaticUpdate && updateInterval > 0 && archiveOrders === null) {
            setTimeLeft(updateInterval * 60); // Reset countdown to full interval in seconds
    
            // Start the update interval
            intervalId = setInterval(() => {
                onUpdate();
                setTimeLeft(updateInterval * 60); // Reset countdown after update
            }, updateInterval * 60 * 1000);
    
            // Start the countdown timer to update every second
            countdownId = setInterval(() => {
                setTimeLeft((prev) => (prev > 0 ? prev - 1 : updateInterval * 60));
            }, 1000);
        }
    
        return () => {
            clearInterval(intervalId);
            clearInterval(countdownId); // Clean up countdown timer
        };
        // eslint-disable-next-line
    }, [automaticUpdate, updateInterval, archiveOrders]);
    
    // Convert seconds to minutes and seconds for display
    const formatTimeLeft = () => {
        const minutes = Math.floor(timeLeft / 60);
        const seconds = timeLeft % 60;
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };
    
    const handleDeleteOrder = async (clientId, orderId) => {
        // Check if clientId and orderId are not empty
        if (!clientId || !orderId) {
            console.warn("Client ID or Order ID is missing. Delete operation aborted.");
            return;
        }

        try {
            const orderRef = doc(db, `Customers/${currentUser.uid}/clients/${clientId}/pending`, orderId);
            await deleteDoc(orderRef);
            console.log(`Order with ID ${orderId} for client ${clientId} deleted successfully`);

            // Update the state to remove the deleted order
            setFilteredAllPending((prevOrders) =>
                prevOrders.filter((order) => !(order.clientId === clientId && order.id === orderId))
            );

            await onUpdate();
        } catch (error) {
            console.error("Error deleting order:", error);
        }
    };
    

    const downloadMapping = () => {
        if (status === "A") {
            downloadExcel(allOrders, "AllActiveOrders.xlsx");
        }
        if (status === "P") {
            downloadExcel(allPending, "AllPendingOrders.xlsx");
        }
    };
    
    
    
    const [orderForm, setOrderForm] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    
    // Function to handle both accountView and selected customer
    const handleViewOrder = (order) => {
        setOrderForm(true);
        setSelectedOrder(order);
    };
    
    const [listView, setListView] = useState(true);

    useEffect(() => {
      const storedPreference = localStorage.getItem('listViewPreference');
      if (storedPreference !== null) {
        setListView(JSON.parse(storedPreference));
      }
    }, []);
    
    
    
    
    
    
    
    
    const [secondaryNavigation, setSecondaryNavigation] = useState([
      // { name: 'Dashboard', href: '#', current: false },
      { name: 'Active Orders', href: 'A', current: false },
      { name: 'Pending Orders', href: 'P', current: true },
    ]);
    
    const LabelMapping = {
        A: "Active Orders",
        P: "Pending Orders",
    };
    
    const stats = [
      { name: `Number of Active Orders`, value: `${filteredAllOrders.length}`, unit: `${countUniqueCustomerCodes(filteredAllOrders)} customers` },
      { name: 'Active Orders', stat: "A",  value: `$ ${numberWithCommas(activeGrandTotal.toFixed(2))}`, unit: `${filteredAllOrders.length}` },
      { name: `Number of Pending Orders`, value: `${fileredAllPending.length}`, unit: `${countUniqueCustomerCodes(fileredAllPending)} customers` },
      { name: 'Pending Orders', stat: "P", value: `$ ${numberWithCommas(pendingGrandTotal.toFixed(2))}`, unit: `${fileredAllPending.length}` },
    ]
    
    const getStatusColor = (status) => {
        switch (status) {
            case 'A':
                return 'bg-green-600/25 text-green-600'; // Active
            case 'P':
                return 'bg-yellow-500/25 text-yellow-500'; // Pending
            default:
                return 'bg-gray-400/25 text-gray-400'; // Fallback
        }
    };
    
    const [collapse, setCollapse] = useState(false);
    
    const toggleCollapse = () =>{
        setCollapse(!collapse);
    }
    
    
    return (
        
        <>
            {archiveOrders !== null && (
                <ManualOrderArchiver
                    activeOrders={archiveOrders}
                    onClose={() => setArchiveOrders(null)}
                    onArchived={handleArchived}
                />
            )}
            {orderForm ? (
                <NewOrder
                    order={selectedOrder}
                    isEditing={true}
                    close={() => setOrderForm(false)}
                />
            ) : (
                <div className={`flex h-[calc(100dvh-3.5rem)] overflow-hidden w-full transition-all duration-500 ease-in-out`}>
        
                    <PlantLoading status={fetchStatus} />

                    
                    {updating && (
                        // <Loading/>
                        <PlantLoading status={fetchStatus} />
                    )}
                    
                    
                    {/*Redesign*/}
                    <div className={`animate-fadeIn z-20 ${darkMode ? 'bg-darkMainColor text-white' : 'bg-white text-black'} w-full`}>
                
                        <main>
                            
                            
                            <header>
                                {/* Secondary navigation */}
                                <nav className={`flex items-center justify-between overflow-x-auto border-b ${darkMode ? 'border-neutral-700' : 'border-gray-300'} py-1.5`}>
                                    <div
                                        role="list"
                                        className="flex w-fit flex-none gap-x-6 px-4 text-sm/6 font-semibold text-gray-400"
                                    >
                                        {secondaryNavigation.map((item) => (
                                            <div
                                              key={item.name}
                                              onClick={() => {
                                                setStatus(item.href);
                                                setSecondaryNavigation((nav) =>
                                                  nav.map((navItem) => ({
                                                    ...navItem,
                                                    current: navItem.href === item.href,
                                                  }))
                                                );
                                              }}
                                              className="cursor-pointer"
                                            >
                                              <div className={item.current ? 'text-green-600' : ''}>
                                                {item.name}
                                              </div>
                                            </div>
                                          ))}
                                    </div>
                                    
                                    
                                    
                                    <Popover className="w-full h-full z-50">
                                        <div className="w-full h-full">
                                            <div className="w-full mx-auto h-full flex items-center px-6 lg:px-8">
                                                <PopoverButton className="absolute right-16 inline-flex mr-3 items-center gap-x-1 text-sm font-semibold">
                                                    Reps <ChevronDownIcon aria-hidden="true" className="size-5" />
                                                </PopoverButton>
                                            </div>
                                        </div>
            
                                        <PopoverPanel
                                            transition
                                            className={`absolute top-28 right-6 border ${darkMode ? 'bg-darkMainColor border-neutral-700' : 'bg-white'} mt-3 rounded-lg shadow-xl transition data-[closed]:-translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in`}
                                        >
                                            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-x-8 px-6 py-10 lg:grid-cols-2 lg:px-8">
            
                                                {/*Column 1*/}
                                                <div className="grid grid-cols-1 gap-x-6 sm:gap-x-8">
                                                    <div className={`w-full`}>
                                                        <h3 className="text-sm/6 font-medium">Sales Reps List</h3>
                                                        <h1 className="text-xs ml-3 text-gray-500 font-medium">These are the Sales
                                                            Reps you are Currently Servicing</h1>
                                                        <div className="mt-6 flow-root">
                                                            <div className="-my-2">
            
                                                                {reps.length === 0 ? (
                                                                    // Display custom dialog if sprays array is empty
                                                                    <div className="flex w-full h-full justify-center items-center py-2">
            
                                                                        <div className={`flex items-center border border-dashed ${darkMode ? 'text-white border-borderColor' : 'text-black bg-neutral-50'} w-fit px-4 py-1 h-fit mr-auto ml-auto justify-center rounded-lg hover:scale-105`}>
                                                                            <div className="p-2 rounded-md shadow-gray-900 flex items-center justify-center flex-col m-2">
                                                                                <LinkSlashIcon className={`w-4 stroke-2 mr-3`}/>
                                                                                <p className="text-xs mt-2 line-clamp-1 tracking-widest flex">You Have No Reps</p>
                                                                                <p className="text-xxs mt-2 line-clamp-1 tracking-widest flex text-gray-500">You may select reps in the profile and settings page</p>
                                                                                
                                                                                <a className={`text-xs bg-green-600 px-2 py-0.5 rounded-lg mt-2 hover:bg-opacity-50`}
                                                                                    href={`/Profile`}
                                                                                >
                                                                                    Go To Settings
                                                                                </a>
                                                                            </div>
                                                                        </div>
            
                                                                    </div>
                                                                ) : (
                                                                    // Display SprayCard components if sprays array is not empty
                                                                    <div className="grid grid-cols-2 gap-2 ml-6">
                                                                        {reps.map((item, index) => (
                                                                            <div key={item.name}
                                                                                 className="py-1 text-sm font-semibold">
                                                                                [{index + 1}] {item.name}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                {/*Column 2*/}
                                                <div className="grid grid-cols-1 w-full sm:gap-4 lg:grid-cols-2">
                                                    
                                                    {/*Automatic Updates*/}
                                                    <Field className="flex w-full col-span-2">
                                                    <Switch
                                                            checked={automaticUpdate}
                                                            onChange={setAutomaticUpdate}
                                                            className="group relative inline-flex h-5 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-transparent"
                                                        >
                                                            <span className="sr-only">Use setting</span>
                                                            <span aria-hidden="true"
                                                                  className="pointer-events-none absolute size-full rounded-md"/>
                                                            <span
                                                                aria-hidden="true"
                                                                className={`pointer-events-none absolute mx-auto h-4 w-9 rounded-full ${darkMode ? 'bg-neutral-700' : 'bg-gray-200'} transition-colors duration-200 ease-in-out group-data-[checked]:bg-green-600`}
                                                            />
                                                            <span
                                                                aria-hidden="true"
                                                                className="pointer-events-none absolute left-0 inline-block size-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out group-data-[checked]:translate-x-5"
                                                            />
                                                        </Switch>
                                                        <Label as="span" className="ml-3 text-sm w-full">
                                                            <span className="font-medium">Automatic Updates</span>{' '}
                                                            <span className="text-gray-500 hidden text-xs">(Specify Time)</span>
                                                        </Label>
                                                    </Field>
                                                    
                                                    {/*Alert Sound*/}
                                                    <Field className="flex w-full col-span-2">
                                                        <Switch
                                                            checked={alertSound}
                                                            onChange={setAlertSound}
                                                            className="group relative inline-flex h-5 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-transparent"
                                                        >
                                                            <span className="sr-only">Use setting</span>
                                                            <span aria-hidden="true"
                                                                  className="pointer-events-none absolute size-full rounded-md"/>
                                                            <span
                                                                aria-hidden="true"
                                                                className={`pointer-events-none absolute mx-auto h-4 w-9 rounded-full ${darkMode ? 'bg-neutral-700' : 'bg-gray-200'} transition-colors duration-200 ease-in-out group-data-[checked]:bg-green-600`}
                                                            />
                                                            <span
                                                                aria-hidden="true"
                                                                className="pointer-events-none absolute left-0 inline-block size-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out group-data-[checked]:translate-x-5"
                                                            />
                                                        </Switch>
                                                        <Label as="span" className="ml-3 text-sm w-full">
                                                            <span className="font-medium">Alert Chime</span>{' '}
                                                            <span className="text-gray-500 text-xs">(Only if pending orders are present)</span>
                                                        </Label>
                                                    </Field>
                                                    
                                                    {/*Time*/}
                                                    <Field className="flex w-full col-span-2 relative items-center">
                                                        <label
                                                            htmlFor="name"
                                                            className={`absolute -top-2 left-2 inline-block rounded-lg ${darkMode ? 'bg-darkMainColor' : 'bg-white text-gray-900'} px-1 text-xs font-medium`}
                                                        >
                                                            Time
                                                        </label>
                                                        <Input
                                                            id="name"
                                                            name="name"
                                                            type="number"
                                                            placeholder="0 mins"
                                                            value={updateInterval}
                                                            disabled={true}
                                                            onChange={(e) => setUpdateInterval(Number(e.target.value))}
                                                            className={`block w-1/2 rounded-md ${darkMode ? 'bg-darkMainColor text-white outline-gray-500' : 'bg-white text-gray-900 outline-gray-300'} px-3 py-1.5 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                        />
                                                        <Label as="span" className="ml-3 text-sm w-full">
                                                            <span className="font-medium">Time</span>{' '}
                                                            <span className="text-gray-500 text-xs">({updateInterval} mins)</span>
                                                        </Label>
                                                    </Field>
                                                    
                                                    {/*CountDown*/}
                                                    <Field className="flex w-full col-span-2 relative items-center">
                                                        <label
                                                            className={`absolute -top-2 left-2 inline-block rounded-lg ${darkMode ? 'bg-darkMainColor' : 'bg-white text-gray-900'} px-1 text-xs font-medium`}
                                                        >
                                                            Remaining Time
                                                        </label>
                                                        <div className={`block w-1/2 rounded-md ${darkMode ? 'bg-darkMainColor text-white outline-gray-500' : 'bg-white text-gray-900 outline-gray-300'} px-3 py-1.5 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}>
                                                            {formatTimeLeft()}
                                                        </div>
                                                        <Label as="span" className="ml-3 text-sm w-full">
                                                            <span className="font-medium">Next Refresh</span>{' '}
                                                        </Label>
                                                    </Field>
                                                    
                                                    
                                                    <div className={`w-full h-full hidden items-center justify-center flex rounded-lg isolate overflow-hidden`}>
                                                        {/* Background Course Image */}
                                                        <img
                                                            alt=""
                                                            src={course}
                                                            className={`absolute border ${darkMode ? 'border-borderColor' : 'drop-shadow'} rounded-lg inset-0 -z-10 h-full w-full object-cover object-right md:object-center`}
                                                        />
                                                        
                                                        {/* Centered Logo */}
                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                            <img
                                                                alt="Logo"
                                                                src={fullLogo}
                                                                className="p-16 object-contain"
                                                            />
                                                        </div>
                                                    </div>
                                                
                                                
                                                </div>
                                            
                                            
                                            </div>
                                        </PopoverPanel>
                                    </Popover>

                                    
                                    <Button
                                        onClick={toggleCollapse}
                                        className={`mx-4`}
                                    >
                                        <ChevronDownIcon className={`${collapse ? 'rotate-180' : ''} transition-all duration-1000 size-6 text-green-600`}/>
                                    </Button>
                                    
                                </nav>
                                
                                {/* Heading */}
                                <div className={`${collapse ? 'hidden' : ''} flex flex-col items-start justify-between gap-x-8 ${darkMode ? 'bg-gray-700/10' : 'bg-neutral-300/50'} px-4 py-1.5 sm:flex-row sm:items-center`}>
                                    <div>
                                        <div className="flex items-center gap-x-3">
                                            <div className={`flex-none rounded-full ${getStatusColor(status)} p-1`}>
                                                <div className="size-2 rounded-full bg-current"/>
                                            </div>
                                            <h1 className="flex gap-x-3 text-base/7">
                                                <span className="font-semibold">Rep Orders</span>
                                                <span className="font-semibold text-gray-500">{getQuarterString()}</span>
                                                <span className="text-gray-600">/</span>
                                                <span className="font-semibold">{LabelMapping[status]}</span>
                                            </h1>
                                        </div>
                                        <p className="text-xs/6 opacity-50 italic">
                                            <span className={`font-bold ml-1`}>
                                                Last Updated: {formatRelativeTime(lastUpdated)} ({formatUnixTimestampToReadableString(lastUpdated)})
                                            </span>
                                        </p>
                                    </div>
                                    <div className={`space-x-3 flex`}>
                                        
                                        {/*<Button onClick={onUpdate} className="flex items-center hover:scale-105 order-first flex-none rounded-full bg-green-600/10 px-2 py-1 text-xs font-medium text-green-600 ring-1 ring-green-600/30 ring-inset sm:order-none">*/}
                                        {/*    <ArrowPathIcon className={`size-4 text-green-600 mr-2`}/>*/}
                                        {/*    Sync Orders*/}
                                        {/*</Button>*/}
                                        
                                        {/*<CustomButton*/}
                                        {/*    onClick={() => setArchiveOrders([...allOrders])}*/}
                                        {/*    color="orange"*/}
                                        {/*    icon={ArchiveBoxIcon}*/}
                                        {/*    text="Archive Orders"*/}
                                        {/*    disabled={updating || fetchStatus.running}*/}
                                        {/*/>*/}
                                        <CustomButton
                                            onClick={onUpdate}
                                            color={`green`}
                                            icon={ArrowPathIcon}
                                            text={`Sync Orders`}
                                        />
                                        
                                        <Button onClick={downloadMapping} className="hidden flex items-center hover:scale-105 order-first flex-none rounded-full bg-green-600/10 px-2 py-1 text-xs font-medium text-green-600 ring-1 ring-green-600/30 ring-inset sm:order-none">
                                            <DocumentArrowDownIcon className={`size-4 text-green-600 mr-2`}/>
                                            Download Excel
                                        </Button>
                                        
                                    </div>
                                </div>
                                
                                {/* Stats */}
                                <div className={`${collapse ? 'hidden' : ''} grid grid-cols-1 ${darkMode ? ' bg-gray-700/10' : 'bg-neutral-300/50'} sm:grid-cols-2 lg:grid-cols-4`}>
                                    
                                    {stats.map((stat, statIdx) => {
                                        const isActive = stat.stat === status;
                                    
                                        // Define a map for border color classes
                                        const statusBorderColor = {
                                            A: 'border-green-600 bg-green-600/15',
                                            P: 'border-yellow-600 bg-yellow-600/15',
                                            Planned: 'border-blue-600 bg-blue-600/15',
                                        };
                                        
                                        const statColors = {
                                          A: 'bg-green-600/25  border-green-600 text-green-600',
                                          P: 'bg-yellow-600/25  border-yellow-600 text-yellow-600',
                                          Planned: 'bg-blue-600/25  border-blue-600 text-blue-600',
                                        };
                                    
                                        // Determine the border color to apply
                                        const activeBorder = isActive ? statusBorderColor[stat.stat] : (darkMode ? '' : '');
                                    
                                        return (
                                            <div
                                                key={stat.name}
                                                className={classNames(
                                                    statIdx % 2 === 1 ? 'sm:border-l' : statIdx === 2 ? 'lg:border-l' : '',
                                                    `border-t ${darkMode ? 'border-neutral-700' : 'border-gray-300'}`
                                                )}
                                            >
                                                <div
                                                    className={`px-4 py-1 ${activeBorder ? ` ${activeBorder}` : ''}`}>
                                                    <p className="text-sm font-bold opacity-50 italic">{stat.name}</p>
                                                    <p className="-mt-1 flex items-baseline gap-x-2">
                                                        <span
                                                            className="text-xl font-semibold tracking-tight">{stat.value}</span>
                                                        {stat.unit ? (
                                                            <span
                                                                className={`text-sm px-2 rounded-lg font-bold ${statColors[stat.stat] || 'text-green-600'}`}
                                                            >
                                                                {stat.unit}
                                                            </span>
                                                        ) : null}
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                
                                
                                </div>
                            
                            </header>
                            
                            {/* Activity list */}
                            <div className="border-t border-white/10">
                                
                                {/*Search bar row*/}
                                <div className={`grid grid-cols-8 w-full h-fit items-center ${darkMode ? 'text-white' : 'text-black'}`}>
                                    
                                    {/*Search Bar*/}
                                    <div className="col-span-4 flex items-center h-fit text-sm">
                                        
                                        {/*<Button*/}
                                        {/*    onClick={() => {*/}
                                        {/*        const newListView = !listView;*/}
                                        {/*        setListView(newListView);*/}
                                        {/*        localStorage.setItem('listViewPreference', JSON.stringify(newListView));*/}
                                        {/*    }}*/}
                                        {/*    className="w-fit rounded-full mr-1.5 size-8 flex items-center justify-center align-middle">*/}
                                        {/*    {listView ? <RectangleGroupIcon className="size-5"/> :*/}
                                        {/*        <QueueListIcon className="size-5"/>}*/}
                                        {/*</Button>*/}
                                        
                                        {/*<div className="text-sm mb-1">Customer</div>*/}
                
                                        <div className={`${darkMode ? 'text-white bg-darkMainColor border-neutral-700' : 'text-black bg-white'} w-full rounded-md flex items-center justify-center`}>
                                            <OrderSearchInput
                                                status={status}
                                                searchQuery={searchQuery}
                                                setSearchQuery={setSearchQuery}
                                                onSearch={
                                                    status === "A" ? handleActiveSearch :
                                                    status === "P" ? handlePendingSearch :
                                                    () => {}
                                                }
                                                darkMode={darkMode}
                                            />
                                        </div>
                
                                    </div>
                                    
                                    <span className="col-span-4 my-0.5 text-gray-500 isolate inline-flex justify-end rounded-md shadow-xs">
                                      <Select
                                          onChange={filtering}
                                          className={`relative gap-x-2 inline-flex items-center rounded-l-md bg-gray-700 bg-opacity-10 ${darkMode ? 'ring-neutral-700' : 'ring-gray-300'} px-3 py-1 text-xs ring-1 ring-inset hover:bg-opacity-25 hover:text-green-600 hover:tracking-wider duration-500 focus:z-10`}
                                      >
                                          <option value="">Select Industry...</option>
                                          <option value="BOTANICAL">Botanical Garden</option>
                                          <option value="DISTRIB">Product Distributor</option>
                                          <option value="EMPLOYEE">PFC-EMPLOYEE</option>
                                          <option value="FARM">Agriculture - Farm</option>
                                          <option value="GOLF">Golf Course</option>
                                          <option value="GOVERNMENT">Government Entity</option>
                                          <option value="INDUSTRIAL">Industrial</option>
                                          <option value="LAWNTREE">Lawn Care & Tree Care</option>
                                          <option value="MRSALE">One Time Purchase Expected</option>
                                          <option value="MUNICIPAL">Municipal/ School</option>
                                          <option value="Non-Profit">Non-Profit Account</option>
                                          <option value="OTHER">Other</option>
                                          <option value="SPORTFLD">Sportsfield</option>
                                      </Select>
                                      <Select
                                          onChange={filteringRep}
                                          className={`relative gap-x-2 -ml-px inline-flex items-center bg-gray-700 bg-opacity-10 ${darkMode ? 'ring-neutral-700' : 'ring-gray-300'} px-3 py-1 text-xs ring-1 ring-inset hover:bg-opacity-25 hover:text-green-600 hover:tracking-wider duration-500 focus:z-10`}
                                      >
                                        <option value="">Select Sales Rep...</option>
                                        
                                        {reps.map((rep, index) => {
                                            const label = rep.name; // Get label from mapping object
                                            return label ? (
                                                <option key={index} value={rep.name}>
                                                    {label}
                                                </option>
                                            ) : null; // Exclude options with no matching label
                                        })}
                                      </Select>
                                      <Button
                                          onClick={toggleAlphaAscDesc}
                                          className={`relative gap-x-2 -ml-px inline-flex items-center bg-gray-700 bg-opacity-10 ${darkMode ? 'ring-neutral-700' : 'ring-gray-300'} px-3 py-1 text-xs ring-1 ring-inset hover:bg-opacity-25 hover:text-green-600 hover:tracking-wider duration-500 focus:z-10`}
                                      >
                                        Customer
                                        <ArrowUpIcon className={`size-4 ${AlphaAscDesc ? '' : 'rotate-180'} duration-500`}/>
                                      </Button>
                                      <Button
                                          onClick={toggleDateAscDesc}
                                          className={`relative gap-x-2 -ml-px inline-flex items-center bg-gray-700 bg-opacity-10 ${darkMode ? 'ring-neutral-700' : 'ring-gray-300'} px-3 py-1 text-xs ring-1 ring-inset hover:bg-opacity-25 hover:text-green-600 hover:tracking-wider duration-500 focus:z-10`}
                                      >
                                        Placed on Date
                                        <ArrowUpIcon className={`size-4 ${DateAscDesc ? '' : 'rotate-180'} duration-500`}/>
                                      </Button>
                                      <Button
                                          onClick={toggleAscDesc}
                                          className={`relative gap-x-2 -ml-px inline-flex items-center rounded-r-md bg-gray-700 bg-opacity-10 ${darkMode ? 'ring-neutral-700' : 'ring-gray-300'} px-3 py-1 text-xs ring-1 ring-inset hover:bg-opacity-25 hover:text-green-600 hover:tracking-wider duration-500 focus:z-10`}
                                      >
                                        Order Total
                                        <ArrowUpIcon className={`size-4 ${AscDesc ? '' : 'rotate-180'} duration-500`}/>
                                      </Button>
                                    </span>
                                    
                                </div>
                                
                                
                                {/*Orders MAP*/}
                                <div className={`overflow-y-auto ${collapse ? 'h-[calc(100dvh-8.2rem)]' : 'h-[calc(100dvh-15.8rem)]'} border-t scrollbar-hide ${darkMode ? 'text-white bg-darkMainColor border-neutral-700' : 'text-black bg-white'} rounded-b-lg scrollbar-hide`}>
                                    
                                    <OrderSection
                                        status={status}
                                        orders={currentStatus.orders || []}
                                        emptyTitle={currentStatus.emptyTitle}
                                        emptyDescription={currentStatus.emptyDescription}
                                        darkMode={darkMode}
                                        onUpdate={onUpdate}
                                        csrRep={fname}
                                        handleViewOrder={handleViewOrder}
                                        onDelete={(customerUID, id) => {
                                            if (status === "Planned") handleDeleteOrder(customerUID, id);
                                        }}
                                        listView={listView}
                                    />
    
                                </div>
        
                                
                                
                            </div>
                            
                        </main>
                    
                    </div>
                    
                    
                </div>
           )}
        </>
        

    )
};


export default PlantOrders;
